<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">Churn</v-toolbar-title>
                    <v-spacer/>
                    <PeriodoFilter
                        v-model="periodo"
                        defaultPeriodo="Últimos 12 meses"
                        @update="consultar"
                    />
                </v-toolbar>

                <v-card elevation="2" class="ma-1">
                    <v-card-text>
                        <!-- Filtro por quantidade de faturas pagas da empresa -->
                        <v-row align="center" dense>
                            <v-col cols="12" sm="auto" class="d-flex align-center">
                                <span class="mr-3 body-2">Faturas pagas:</span>
                                <v-text-field
                                    v-model="faturasMin"
                                    label="De"
                                    type="number"
                                    min="0"
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    class="mr-2"
                                    style="max-width: 120px;"
                                />
                                <v-text-field
                                    v-model="faturasMax"
                                    label="Até"
                                    type="number"
                                    min="0"
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    style="max-width: 120px;"
                                />
                            </v-col>
                        </v-row>

                        <v-progress-linear
                            v-if="carregando"
                            indeterminate
                            color="primary"
                            class="mb-2"
                        />

                        <v-alert
                            v-if="temFiltroFaturas"
                            dense
                            text
                            type="info"
                            class="body-2"
                        >
                            Mostrando apenas cancelamentos de empresas com {{ descricaoFiltroFaturas }}.
                            A base ativa continua sendo a base inteira, então a taxa de churn é a
                            fatia dela que cancelou dentro dessa faixa.
                        </v-alert>

                        <!-- Cards -->
                        <v-row>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="String(resumo.total_cancelamentos)"
                                    label="Cancelamentos"
                                    color="error"
                                    icon="mdi-account-remove"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`R$ ${formatPrice(resumo.mrr_perdido)}`"
                                    label="MRR Perdido"
                                    color="red darken-3"
                                    icon="mdi-cash-remove"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`${resumo.taxa_churn}%`"
                                    label="Taxa de Churn"
                                    color="orange darken-2"
                                    icon="mdi-percent"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="String(resumo.base_ativa_inicio_periodo)"
                                    label="Base Ativa (inicio periodo)"
                                    color="blue-grey"
                                    icon="mdi-account-group"
                                />
                            </v-col>
                        </v-row>

                        <!-- Churn Mensal -->
                        <v-row>
                            <v-col cols="12" md="8">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Churn Mensal
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartChurnMensal" height="100"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Taxa de Churn Mensal
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartTaxaChurn" height="200"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Motivos -->
                        <v-row>
                            <v-col cols="12" md="5">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Motivos de Cancelamento
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartMotivos" height="200"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="7">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Motivos de Cancelamento
                                    </div>
                                    <v-data-table
                                        :headers="headersMotivos"
                                        :items="motivos"
                                        :mobileBreakpoint="0"
                                        hide-default-footer
                                        :items-per-page="-1"
                                        dense
                                    >
                                        <template v-slot:item.mrr_perdido="{ item }">
                                            R$ {{ formatPrice(item.mrr_perdido) }}
                                        </template>
                                        <template v-slot:item.percentual="{ item }">
                                            {{ item.percentual }}%
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Churn por Mes Tabela -->
                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Cancelamentos por Mes
                                    </div>
                                    <v-data-table
                                        :headers="headersMensal"
                                        :items="churnMensal"
                                        :mobileBreakpoint="0"
                                        hide-default-footer
                                        :items-per-page="-1"
                                        dense
                                        style="cursor: pointer;"
                                        @click:row="abrirDetalheMes"
                                    >
                                        <template v-slot:item.mrr_perdido="{ item }">
                                            R$ {{ formatPrice(item.mrr_perdido) }}
                                        </template>
                                        <template v-slot:item.taxa_churn="{ item }">
                                            {{ item.taxa_churn }}%
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Cancelamentos Detalhados -->
                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Cancelamentos Detalhados
                                        <v-chip
                                            v-if="mesFiltro"
                                            class="ml-2"
                                            small
                                            close
                                            color="orange darken-2"
                                            text-color="white"
                                            @click:close="mesFiltro = null"
                                        >
                                            Mês: {{ mesFiltro }}
                                        </v-chip>
                                    </div>
                                    <div class="subtitle-2 mb-2" style="text-align: center;">
                                        {{ detalhadoFiltrado.length }} cancelamentos
                                        | MRR Perdido: R$ {{ formatPrice(mrrPerdidoFiltrado) }}
                                    </div>
                                    <v-data-table
                                        :headers="headersDetalhado"
                                        :items="detalhadoFiltrado"
                                        :mobileBreakpoint="0"
                                        :items-per-page="15"
                                        dense
                                    >
                                        <template v-slot:item.mrr_perdido="{ item }">
                                            R$ {{ formatPrice(item.mrr_perdido) }}
                                        </template>
                                        <template v-slot:item.cancelado_at="{ item }">
                                            {{ formatDate(item.cancelado_at, 'DD/MM/YYYY') }}
                                        </template>
                                        <template v-slot:item.empresa_id="{ item }">
                                            <a @click.stop="$router.push(`/financeiro/${item.empresa_id}`)">
                                                {{ item.empresa_id }}
                                            </a>
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer.vue';
import Card from '@/views/home/Card.vue';
import PeriodoFilter from '@/components/PeriodoFilter.vue';
import Chart from 'chart.js';

export default {
    name: 'RelatorioChurn',
    components: { BaseContainer, Card, PeriodoFilter },

    data: () => ({
        periodo: {},
        faturasMin: null,
        faturasMax: null,
        timerFaturas: null,
        carregando: false,
        resumo: { total_cancelamentos: 0, mrr_perdido: 0, taxa_churn: 0, base_ativa_inicio_periodo: 0 },
        churnMensal: [],
        motivos: [],
        detalhado: [],
        detalheTotais: { mrr_perdido: 0 },
        mesFiltro: null,
        charts: {},
        headersMotivos: [
            { text: 'Motivo', value: 'motivo' },
            { text: 'Qtd', value: 'qtd' },
            { text: 'MRR Perdido', value: 'mrr_perdido' },
            { text: '%', value: 'percentual' },
        ],
        headersMensal: [
            { text: 'Mes', value: 'mes' },
            { text: 'Cancelamentos', value: 'qtd_cancelamentos' },
            { text: 'MRR Perdido', value: 'mrr_perdido' },
            { text: 'Base Ativa', value: 'base_ativa_inicio_mes' },
            { text: 'Taxa Churn', value: 'taxa_churn' },
        ],
        headersDetalhado: [
            { text: 'ID', value: 'empresa_id' },
            { text: 'Empresa', value: 'fantasia' },
            { text: 'MRR Perdido', value: 'mrr_perdido' },
            { text: 'Motivo', value: 'motivo' },
            { text: 'Cancelado em', value: 'cancelado_at' },
            { text: 'Pgtos Realizados', value: 'qtd_cobrancas_pagas' },
            { text: 'Descricao', value: 'cancelamento_descricao' },
        ],
    }),

    computed: {
        temFiltroFaturas() {
            return this.faturaValida(this.faturasMin) || this.faturaValida(this.faturasMax);
        },
        descricaoFiltroFaturas() {
            const min = this.faturaValida(this.faturasMin) ? Number(this.faturasMin) : null;
            const max = this.faturaValida(this.faturasMax) ? Number(this.faturasMax) : null;

            if (min !== null && max !== null) {
                const de = Math.min(min, max);
                const ate = Math.max(min, max);

                return de === ate
                    ? `exatamente ${de} fatura(s) paga(s)`
                    : `de ${de} a ${ate} faturas pagas`;
            }

            if (min !== null) return `${min} fatura(s) paga(s) ou mais`;

            return `até ${max} fatura(s) paga(s)`;
        },
        detalhadoFiltrado() {
            if (!this.mesFiltro) return this.detalhado;
            return this.detalhado.filter(
                r => this.moment(r.cancelado_at).format('MM/YYYY') === this.mesFiltro
            );
        },
        mrrPerdidoFiltrado() {
            if (!this.mesFiltro) return this.detalheTotais.mrr_perdido || 0;
            return this.detalhadoFiltrado.reduce((acc, r) => acc + Number(r.mrr_perdido || 0), 0);
        },
    },

    watch: {
        faturasMin() {
            this.agendarConsulta();
        },
        faturasMax() {
            this.agendarConsulta();
        },
    },

    mounted() {
        this.$nextTick(() => this.consultar());
    },

    beforeDestroy() {
        clearTimeout(this.timerFaturas);
    },

    methods: {
        async consultar() {
            let params = `start=${this.periodo.start || ''}&end=${this.periodo.end || ''}`;

            if (this.faturaValida(this.faturasMin)) {
                params += `&faturas_min=${Number(this.faturasMin)}`;
            }

            if (this.faturaValida(this.faturasMax)) {
                params += `&faturas_max=${Number(this.faturasMax)}`;
            }

            this.carregando = true;

            try {
                const [resumo, mensal, motivosResp, detalhadoResp] = await Promise.all([
                    this.$http.get(`relatorios/churn/resumo?${params}`),
                    this.$http.get(`relatorios/churn/mensal?${params}`),
                    this.$http.get(`relatorios/churn/motivos?${params}`),
                    this.$http.get(`relatorios/churn/detalhado?${params}`),
                ]);

                this.resumo = resumo.data.data;
                this.churnMensal = mensal.data.data;
                this.motivos = motivosResp.data.data;
                this.detalhado = detalhadoResp.data.data;
                this.detalheTotais = detalhadoResp.data.totais || {};

                this.gerarGraficos();
            } catch (e) {
                console.error(e);
                this.notify('Erro ao carregar relatório de churn', 'warning');
            } finally {
                this.carregando = false;
            }
        },

        gerarGraficos() {
            // Churn mensal - barras
            const mensalOrdenado = [...this.churnMensal].reverse();
            this.renderChart('chartChurnMensal', {
                type: 'bar',
                labels: mensalOrdenado.map(r => r.mes),
                datasets: [
                    {
                        label: 'Cancelamentos',
                        data: mensalOrdenado.map(r => r.qtd_cancelamentos),
                        backgroundColor: 'rgba(244, 67, 54, 0.7)',
                    },
                ],
            });

            // Taxa de churn - linha
            this.renderChart('chartTaxaChurn', {
                type: 'line',
                labels: mensalOrdenado.map(r => r.mes),
                datasets: [
                    {
                        label: 'Taxa Churn (%)',
                        data: mensalOrdenado.map(r => r.taxa_churn),
                        borderColor: 'rgba(255, 152, 0, 1)',
                        backgroundColor: 'rgba(255, 152, 0, 0.1)',
                        fill: true,
                    },
                ],
            });

            // Motivos - pizza
            const colors = [
                '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
                '#2196F3', '#00BCD4', '#4CAF50', '#FF9800', '#795548',
            ];
            this.renderChart('chartMotivos', {
                type: 'pie',
                labels: this.motivos.map(r => r.motivo),
                datasets: [{
                    data: this.motivos.map(r => r.qtd),
                    backgroundColor: this.motivos.map((_, i) => colors[i % colors.length]),
                }],
            });
        },

        renderChart(ref, config) {
            if (this.charts[ref]) this.charts[ref].destroy();
            const ctx = this.$refs[ref];
            if (!ctx) return;

            this.charts[ref] = new Chart(ctx, {
                type: config.type,
                data: {
                    labels: config.labels,
                    datasets: config.datasets,
                },
                options: {
                    responsive: true,
                    ...(config.type !== 'pie' ? {
                        scales: {
                            yAxes: [{ ticks: { beginAtZero: true } }],
                        },
                    } : {}),
                },
            });
        },

        agendarConsulta() {
            clearTimeout(this.timerFaturas);
            this.timerFaturas = setTimeout(() => this.consultar(), 600);
        },

        faturaValida(valor) {
            return valor !== null && valor !== '' && !isNaN(valor) && Number(valor) >= 0;
        },

        abrirDetalheMes(item) {
            // Alterna o filtro da tabela detalhada pelo mês clicado
            this.mesFiltro = this.mesFiltro === item.mes ? null : item.mes;
        },
    },
};
</script>
